import debounce from 'lodash/debounce';
import LoadingIcon from '../../generic/loading-icon';

const filters = [
  { id: 'filter-fuel', 'name': 'fuel[]', type: 'multiselect'},
  { id: 'filter-body_type', 'name': 'body_type[]', type: 'multiselect'},
  { id: 'filter-gear', 'name': 'gear[]', type: 'multiselect'},
  { id: 'brands-hidden-filter', name: 'brands[]', type: 'hidden' },
  { id: 'models-hidden-filter', name: 'models[]', type: 'hidden' },
]

/**
 * ModelPageSearch add a search functionality to prepresented brand pages
 */
class ModelPageSearch {

  /**
   * @property {HTMLElement} element - This component's scope, everething this class does happens inside this element.
   */
  #element;

  /**
   * @property {object} dom - ModePageSearch component's elements
   */
  #dom = {}

  /**
   * @property {number} page - Page attribute is used to control StageQL limit clause, amount of search result in one set can be changed on the server side.
   */
  #page = 0;

  /**
   * @property {object} templates - Card card mustache template and it's partials
   */
  #templates;

  /**
   * @constructor
   * @param {HTMlElement} element element bind ModelPageSearch logic
   */
  constructor( element ) {

    this.#element = element;
    this.#findElements(element);
    this.#bindEventListeners();

    //Debounced version of the search method, just for recuding unnecessary calls to the server side. Multiselect element has no way to prevent
    //selection event in situation when option are selected programatically. This an issue specially with the brand & model multiselect elements.
    this.debouncedSearch = debounce(this.search, 100, { trailing: true, leading: false });

    //This for infinite scroll
    this.#initIntersectionObserver();

    //Expected search results maximum amount
    this.searchResultsAmount = parseInt( this.#element.dataset.searchresultamount );

    this.#startInterSectionObservation();
    this.#dom.loadingOverlay.classList.toggle('modal-page-search-loading-overlay--hide');
  }

  /**
   * Does a car search to the Stage
   *
   * @async
   * @param {object} options options object
   * @param {boolean} options.append  default false, appends carsearch__results are instead overwriting it
   */
  async search(options = {}) {

    //Stop intersection observation during the search, prevent stopping search if user scroll up and down, up and down at the bottom of the page
    this.#stopIntersectionObservation();

    //Each search has it's own AbortController isntance so they don't mixup between request.
    this._controller = new window.AbortController();

    //Get request's is formed by using this
    const formData = this.#getSearchURL();

    try {
      const searchGetParameters = formData.toString();

      //Delete things that are not needed in the browser's URL address bar
      formData.delete('forceBrand');
      formData.delete('page');
      formData.delete('brands[]');
      formData.delete('models[]');
      formData.delete('carsSearchType');

      history.pushState(null, null, '?' + formData.toString());

      this.#setLoading(true); //Spinning the spinners

      //Send request and wait a moment for response and json.
      const response = await this.#send(searchGetParameters, this._controller);
      const responseJSON = await response.json();

      const html = responseJSON.vehicles
        .map(car => {
          const carCardTemplateParts = this.#templates.basicCard;
          car.cardCustomClasses = carCardTemplateParts.cardCustomClasses;

          if( car?.labels?.some( label => label?.name?.length > 0 ) ) {
            car.cardCustomClasses += ' card--label';
          }

          return window.Dali.render(carCardTemplateParts.template, car, carCardTemplateParts.partials);
        })
        .join(' ');

      if (options.append) {
        this.#dom.carSearchResults.insertAdjacentHTML('beforeend', html);
      } else {
        this.#dom.carSearchResults.innerHTML = html;
      }

      //Can't be placed to finally block due "AbortError", abort error is thrown to stop last fetch
      this.#setLoading(false);

      //Operation performed after each succesfull search is done there..
      this.#afterSearch(responseJSON);

    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Fetch aborted', error); //This occures when a AbortController aborts fetch.
      } else {
        console.warn('Unhandled error:', error);
      }
    }
  }

  /**
   * Setter for the loading attribute. Shown or hides the loading spinner when car search is loading.
   * @private
   * @param {boolean} value true is loading false if not
   */
  #setLoading(value) {
    this._loading = value;
    LoadingIcon.instances['model-page-loading-icon'].show = value;
  }

  // ---- Private functions ----

  /**
   * Send ajax request via fetch API to the Stage
   * @async
   *
   * @param {string} formData get parameters
   * @param {AbortController} controller abort controller signal object
   * @return {Promise} resolved after successful call
   */
  async #send(formData, controller) {
    return await fetch(this.#getBaseURL() + formData, {
      method: 'GET',
      signal: controller.signal,
    });
  }

  /**
   * Preload elements from to DOM to be more accessable
   * @private
   * @param {HTMLElement} element element passed in the constructor
   */
   #findElements(element) {
    this.#dom.form = element.querySelector('form');
    this.#templates = JSON.parse( element.querySelector('.mustache-car-card-templates').innerText );
    this.#dom.categories = element.querySelectorAll('input[name="category"]');
    this.#dom.carSearchResults = element.querySelector('.model-page-search-result .cars');
    this.#dom.greendriveCheckbox = element.querySelector('input[name="greendrive"]');
    this.#dom.offercarsCheckbox = element.querySelector('input[name="offercars"]');
    this.#dom.fetchMoreBtn = element.querySelector('#fetch-more-button button');
    this.#dom.intersectionObservableElement = document.querySelector('.model-page-search-footer');
    this.#dom.noMoreSearchResult = document.querySelector('.no-more-search-result');
    this.#dom.extraLinks = element.querySelector('.extra-links');
    this.#dom.loadingOverlay = element.querySelector('.modal-page-search-loading-overlay');
    this.#dom.resetBtn = element.querySelector('button[type="reset"]');
    this.#dom.hideButton = element.querySelector('button.hide-button');

    this.#dom.hiddenFilterElements = Object.keys( filters )
      .filter( key => filters[key].type === 'hidden' )
      .map( key => {
        return element.querySelector(`input[name="${filters[key].name}"]`);
      }).filter( Boolean );
  }

   /**
   * Aborts last made search before the next search is started.
   * @private
   */
   #abortSearch() {
     if (this._controller) {
       this._controller.abort();
     }
     this.#setLoading(false);
   }

  /**
   * Bind event listeners to HTMLelements
   * @private
   */
  #bindEventListeners() {

     //This way we create only one copy of these functions
     const onSelectChanges = this.#onSelectChanges.bind(this);
     const onValueChanges = this.#onValueChanges.bind(this);

     filters.filter( filter => filter.type === 'multiselect' ).forEach( filter => {
       window.multiselects?.[filter.id]?.addCallback({ onValueChange: onSelectChanges });
     });

     this.#dom.greendriveCheckbox?.addEventListener('change', onValueChanges );

     this.#dom.categories.forEach( category => category.addEventListener('change', onValueChanges) );

     this.#dom.fetchMoreBtn?.addEventListener('click', async () => {
       await this.#fetchMoreSearchResults();
     });

     this.#dom.form.addEventListener('reset', this.#resetForm.bind(this));

     this.#dom.hideButton?.addEventListener('click', this.#hideButtonClicked.bind(this));
   }

  #hideButtonClicked() {    
    this.#element.classList.toggle('hidden');
  }

  /**
   * Fetch more search results.
   * @private
   */
  async #fetchMoreSearchResults() {
    this.#page += 1;
    await this.search({ append: true });
  }

  /**
   * Called on form is reseted by pressing "Tyhjennä valinnat" -button
   * @param {Event} event form reset event
   */
  #resetForm(event) {
    event.preventDefault();

    this.#dom.categories.forEach( category => {
      category.checked = false;
    });

    this.#dom.greendriveCheckbox.checked = false;
    this.#dom.offercarsCheckbox.checked = false;

    filters.filter( filter => filter.type === 'multiselect' ).forEach( filter => {
      window.multiselects?.[filter.id]?.unSelectAll(true);
    });
  }

  /**
   * When a category or greendrive checkbox value changes this is called.
   * @private
   */
  #onValueChanges() {
    this.#page = 0;
    this.#stopIntersectionObservation();
    this.#showFetchMoreBtn(false);
    this.#emptySearchResults();
    this.#showNoMoreSearchResult(false);
    this.#abortSearch();
    this.debouncedSearch();
  }

  /**
   * Empty search result area from loaded cars
   * @private
   */
  #emptySearchResults() {
    this.#dom.carSearchResults.innerHTML = '';
  }

  /**
   * Build search url address
   * @private
   * @return {URLSearchParams} formData
   */
   #getSearchURL() {

    const formData = new URLSearchParams();

    filters.filter( filter => filter.type === 'multiselect' ).forEach( filter => {

      const value = window.multiselects[ filter.id ]?.getValue();

      if( value && value.length > 0 ) {
        value.forEach(value => {
          formData.append(filter.name, value);
        });
      }
    });

    //This is used by ModelPageSearch brand & model
    this.#dom.hiddenFilterElements.forEach( hiddenFilterElement => {
      formData.append(hiddenFilterElement.name, hiddenFilterElement.value);
    });

    //Currentbrand add more filters like locations on the Stage side based on current brand.
    formData.append('forceBrand', document.body.dataset.brand);
    formData.append('page', this.#page );
    formData.append('category[]', this.#dom.form.category.value );
    formData.append('carsSearchType', this.#dom.form.category.value === 'tilattavat-autot' ? 'ah' : 'nw' );


    return formData;
  }

   /**
   * Fetch request base URL-address
   * @private
   * @return {string} ajax request base url
   */
   #getBaseURL() {
     return '/stage-ajax/2022-car-search?';
   }

  /**
   * Called after search
   * @private
   * @param {object} responseJSON Model page search response object
   */
  #afterSearch(responseJSON) {
     if( responseJSON.vehicles.length === 0 ) {
       this.#showNoMoreSearchResult(true);
       this.#showFetchMoreBtn(false);
       this.#stopIntersectionObservation();
     } else {
       this.#showNoMoreSearchResult(false);

       if( responseJSON.paging.currentPage >= 3 ) {
         this.#showFetchMoreBtn(true);
         this.#stopIntersectionObservation();
       } else {
         this.#startInterSectionObservation();
       }
     }

     this.#dom.extraLinks.classList.toggle('hidden', !responseJSON.showStorageSearchLink)
   }

  /**
   * When a change event happens in any multiselect element
   * this function is called.
   * @private
   * @param {object} multiselectData selected data
   */
   #onSelectChanges(multiselectData) {
    this.#page = 0;
    this.#emptySearchResults();
    this.#showNoMoreSearchResult(false);
    this.#showFetchMoreBtn(false);
    this.#stopIntersectionObservation();
    this.#abortSearch();
    this.debouncedSearch();
  }

  /**
   * Show or hide fetch more button bottom of the search results
   * @private
   * @param {boolean} show true to show false to hide
   */
  #showFetchMoreBtn( show ) {
     this.#dom.fetchMoreBtn.classList.toggle('in', show );
   }

  /**
   * Show or hide no more search result text bottom of the search results
   * @private
   * @param {boolean} show true to show false to hide
   */
  #showNoMoreSearchResult( show ) {
    this.#dom.noMoreSearchResult.classList.toggle('hidden', !show );
  }

  /**
   * Intersection observer looks to the view port for a particular element, in this case it's the fetch more button element.
   * Observing is started after fetch more button is pressed once by the user, and stopped after after filter is changed or
   * all search results are fetched from the server.
   * @private
   */
  #initIntersectionObserver() {
    this._observer = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if(entry.isIntersecting){
          this.#fetchMoreSearchResults();
        }
      });

    }, {threshold: 1});
  }

  /**
   * Stop intersection observing, don't load more search result automatically when scrolling down.
   * @private
   */
  #stopIntersectionObservation() {
    this._observer.unobserve(this.#dom.intersectionObservableElement);
  }

  /**
   * Start intersection observing
   * @private
   */
  #startInterSectionObservation() {
    this._observer.observe(this.#dom.intersectionObservableElement);
  }

  // --- static methods ---

  /**
   * Initializes all ModelPageSearch components in document object model
   * @returns {ModelPageSearch?} instance of ModelPageSearch
   */
  static init() {
    const modelPageSearchElement = document.querySelector('.model-page-search');

    if( modelPageSearchElement ) {
      return new ModelPageSearch(modelPageSearchElement);
    }
  }
}

export default ModelPageSearch;
